import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useHome } from '@entities/home';
import { kbApi, kbKeys, type KbOverview } from '@entities/knowledge';
import { IconCheck, plural } from '@shared/ui/v2';
import { onWatchChange, readWatch, unwatchKb, watchKb } from './watch';
import './kb-watch.css';

interface Done {
  aid: string;
  ready: number;
  errors: number;
  site: boolean;
  rub: number;
}

const busyOf = (o: KbOverview) =>
  o.sources.filter((s) => s.state === 'processing' || s.state === 'queued');

function rub(n: number) {
  return (
    new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      n,
    ) + ' ₽'
  );
}

/**
 * Следит за базами знаний, где идёт обработка, и показывает одно уведомление,
 * когда обработано всё (на любой странице кабинета). Монтируется в AppShell.
 */
export function KbWatcher() {
  const qc = useQueryClient();
  const [watch, setWatch] = useState(readWatch);
  const [done, setDone] = useState<Done | null>(null);
  useEffect(() => onWatchChange(() => setWatch(readWatch())), []);

  const aids = Object.keys(watch);
  const results = useQueries({
    queries: aids.map((aid) => ({
      queryKey: kbKeys.overview(aid),
      queryFn: () => kbApi.overview(aid),
      refetchInterval: 4000,
      retry: 1,
    })),
  });

  const sig = results.map((r) => r.dataUpdatedAt + ':' + r.errorUpdatedAt).join('|');
  useEffect(() => {
    aids.forEach((aid, i) => {
      const r = results[i];
      if (!r) return;
      if (r.isError) {
        // Ассистент удалён или нет доступа — перестаём следить.
        const st = (r.error as { response?: { status?: number } })?.response?.status;
        if (st === 403 || st === 404) unwatchKb(aid);
        return;
      }
      const o = r.data;
      // Данные из кеша, полученные до запуска, не в счёт — ждём свежие.
      if (!o || r.dataUpdatedAt <= (watch[aid]?.at ?? 0)) return;
      const busy = busyOf(o);
      if (busy.length) {
        watchKb(
          aid,
          busy.map((s) => s.id),
        );
        return;
      }
      const ids = new Set(watch[aid]?.ids ?? []);
      const mine = o.sources.filter((s) => ids.has(s.id));
      const ready = mine.filter((s) => s.state === 'ready');
      const errors = mine.filter((s) => s.state === 'error');
      unwatchKb(aid);
      qc.invalidateQueries({ queryKey: kbKeys.suggestion(aid) });
      qc.invalidateQueries({ queryKey: ['cabinet'] });
      qc.invalidateQueries({ queryKey: kbKeys.funds });
      if (!ready.length && !errors.length) return; // всё удалили — сообщать нечего
      setDone({
        aid,
        ready: ready.length,
        errors: errors.length,
        site: mine.some((s) => s.type === 'website'),
        rub: ready.reduce((sum, s) => sum + Number(s.charged_rub || 0), 0),
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  if (!done) return null;
  return <DoneToast d={done} onClose={() => setDone(null)} />;
}

function DoneToast({ d, onClose }: { d: Done; onClose: () => void }) {
  const loc = useLocation();
  const { data: home } = useHome();
  const name = home?.assistants.find((a) => a.id === d.aid)?.name;
  const onAssistant = loc.pathname.startsWith(`/assistants/${d.aid}`);
  const onKb = loc.pathname.startsWith(`/assistants/${d.aid}/knowledge`);
  // undefined — ещё узнаём (строку не показываем), null — предложения нет.
  const [sugg, setSugg] = useState<string | null | undefined>(d.ready ? undefined : null);

  useEffect(() => {
    if (!d.ready) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Предложение готовится после обработки (и его же может запрашивать открытая
    // «Настройка» — тогда ответ «ещё готовим»): спрашиваем повторно.
    const ask = (left: number) =>
      kbApi
        .suggestion(d.aid)
        .then((s) => {
          if (!alive) return;
          if (s.pending && left > 0) {
            timer = setTimeout(() => ask(left - 1), 3000);
            return;
          }
          setSugg(s.items.length && !s.dismissed ? (s.topics ?? '') : null);
        })
        .catch(() => alive && setSugg(null));
    ask(8);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [d.aid, d.ready]);

  const word = d.site
    ? ['источник', 'источника', 'источников']
    : ['документ', 'документа', 'документов'];
  const okTitle = d.ready ? 'База знаний готова' : 'Не удалось обработать';
  const lines: string[] = [];
  if (d.ready)
    lines.push(
      `${d.ready} ${plural(d.ready, word[0], word[1], word[2])} ${d.ready === 1 ? 'обработан' : 'обработаны'} · списано ${rub(d.rub)}`,
    );
  if (d.errors)
    lines.push(
      `${d.errors} ${plural(d.errors, 'файл', 'файла', 'файлов')} с ошибкой — деньги за ${d.errors === 1 ? 'него' : 'них'} не списаны`,
    );

  return (
    <div role="status" className="kbw-toast">
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <span className={d.ready ? 'kbw-ok' : 'kbw-err'}>
          {d.ready ? <IconCheck size={12} /> : '!'}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{okTitle}</div>
          {!onAssistant && name && <div className="v2-xs v2-muted">Ассистент: {name}</div>}
          {lines.map((l) => (
            <div key={l} className="v2-xs v2-muted">
              {l}
            </div>
          ))}
        </div>
        <button
          type="button"
          className="v2-icon-btn"
          style={{ width: 26, height: 26 }}
          onClick={onClose}
          aria-label="Закрыть"
        >
          ×
        </button>
      </div>
      {typeof sugg === 'string' && (
        <div className="kbw-sugg">
          <b>ИИ изучил базу знаний и предлагает дополнить инструкцию</b>
          {sugg ? `: ${sugg}.` : '.'}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="v2-btn" onClick={onClose}>
          {typeof sugg === 'string' ? 'Позже' : 'Хорошо'}
        </button>
        {typeof sugg === 'string' ? (
          <Link
            to={`/assistants/${d.aid}/settings`}
            className="v2-btn is-primary"
            onClick={onClose}
          >
            Посмотреть предложение
          </Link>
        ) : (
          !onKb && (
            <Link
              to={`/assistants/${d.aid}/knowledge`}
              className="v2-btn is-primary"
              onClick={onClose}
            >
              Открыть базу знаний
            </Link>
          )
        )}
      </div>
    </div>
  );
}
